// src/pages/MLInsightsPage.js
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, Tooltip, Legend } from 'chart.js';
import { ml } from '../utils/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, Tooltip, Legend);

const CHART_OPTS = {
  plugins: { legend: { labels: { color: '#94a3b8' } } },
  scales: { x: { ticks: { color: '#64748b' }, grid: { color: '#1e2d45' } }, y: { ticks: { color: '#64748b' }, grid: { color: '#1e2d45' } } }
};

export default function MLInsightsPage() {
  const [tab, setTab] = useState('anomaly');

  const { data: anomaly } = useQuery({ queryKey: ['mlAnomaly'], queryFn: () => ml.anomaly().then(r => r.data.data), enabled: tab === 'anomaly' });
  const { data: clusters } = useQuery({ queryKey: ['mlClusters'], queryFn: () => ml.clusters().then(r => r.data.data), enabled: tab === 'clusters' });
  const { data: deviceInsights } = useQuery({ queryKey: ['mlDeviceInsights'], queryFn: () => ml.deviceInsights().then(r => r.data.data), enabled: tab === 'devices' });
  const { data: regPatterns } = useQuery({ queryKey: ['mlRegPatterns'], queryFn: () => ml.regPatterns().then(r => r.data.data), enabled: tab === 'patterns' });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">🧠 ML Insights</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>
            Analytics, anomaly detection, and pattern recognition
          </p>
        </div>
      </div>

      <div className="tabs">
        {[
          { id: 'anomaly',  label: '⚠️ Anomaly Detection' },
          { id: 'clusters', label: '🔵 Student Clusters' },
          { id: 'devices',  label: '📱 Device Intelligence' },
          { id: 'patterns', label: '🔢 Reg. Patterns' },
        ].map(t => (
          <div key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</div>
        ))}
      </div>

      {/* Anomaly Detection */}
      {tab === 'anomaly' && anomaly && (
        <div>
          {anomaly.studentsWithManyDevices.length > 0 && (
            <div className="alert alert-danger" style={{ marginBottom: 16 }}>
              ⚠️ <strong>{anomaly.studentsWithManyDevices.length} students</strong> have 4+ registered devices. Review for potential policy violations.
            </div>
          )}

          <div className="chart-grid" style={{ marginBottom: 20 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Students with 4+ Devices</span></div>
              {anomaly.studentsWithManyDevices.length === 0 ? (
                <div className="empty-state" style={{ padding: 24 }}>✅ No anomalies detected</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Reg No</th><th>Name</th><th>Device Count</th><th>Last Registered</th></tr></thead>
                    <tbody>
                      {anomaly.studentsWithManyDevices.map(s => (
                        <tr key={s.registration_no}>
                          <td className="text-mono">{s.registration_no}</td>
                          <td>{s.full_name}</td>
                          <td><span className={`badge ${s.device_count >= 6 ? 'badge-danger' : 'badge-warning'}`}>{s.device_count}</span></td>
                          <td className="text-mono">{new Date(s.last_registered).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">OUI Concentration Anomalies</span></div>
              {anomaly.ouiConcentration.length === 0 ? (
                <div className="empty-state" style={{ padding: 24 }}>✅ No OUI concentration anomalies</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>OUI Prefix</th><th>Students</th><th>Devices</th></tr></thead>
                    <tbody>
                      {anomaly.ouiConcentration.map(o => (
                        <tr key={o.oui_prefix}>
                          <td className="text-mono">{o.oui_prefix}</td>
                          <td>{o.student_count}</td>
                          <td>{o.device_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {anomaly.spikeDays.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Registration Spike Days (2+ SD above mean)</span></div>
              <div className="alert alert-info" style={{ marginBottom: 12 }}>
                Baseline: avg {anomaly.baseline.mean} registrations/day, σ = {anomaly.baseline.std}
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Date</th><th>Registrations</th><th>Deviation</th></tr></thead>
                  <tbody>
                    {anomaly.spikeDays.map(d => (
                      <tr key={d.day}>
                        <td className="text-mono">{d.day}</td>
                        <td><span className="badge badge-warning">{d.registrations}</span></td>
                        <td>{((d.registrations - parseFloat(anomaly.baseline.mean)) / parseFloat(anomaly.baseline.std)).toFixed(1)}σ above mean</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Clusters */}
      {tab === 'clusters' && clusters && (
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><span className="card-title">Course × Academic Year Distribution</span></div>
            <Bar
              data={{
                labels: [...new Set(clusters.courseXYear.map(r => r.course))],
                datasets: [...new Set(clusters.courseXYear.map(r => r.year))].map((year, i) => ({
                  label: year,
                  data: [...new Set(clusters.courseXYear.map(r => r.course))].map(course => {
                    const found = clusters.courseXYear.find(r => r.course === course && r.year === year);
                    return found?.count || 0;
                  }),
                  backgroundColor: ['#00d4ff','#7c83fd','#00e676','#ffab40'][i % 4],
                  borderRadius: 3
                }))
              }}
              options={{ ...CHART_OPTS, responsive: true }}
            />
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Course × Nationality (Top 50)</span></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Course</th><th>Nationality</th><th>Count</th></tr></thead>
                <tbody>
                  {clusters.courseXNationality.map((r, i) => (
                    <tr key={i}><td>{r.course}</td><td>{r.nationality}</td><td>{r.count}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Device Intelligence */}
      {tab === 'devices' && deviceInsights && (
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><span className="card-title">Device Type by Academic Year</span></div>
            <Bar
              data={{
                labels: [...new Set(deviceInsights.deviceTypeByYear.map(r => r.year))],
                datasets: ['laptop','phone','tablet','other'].map((type, i) => ({
                  label: type,
                  data: [...new Set(deviceInsights.deviceTypeByYear.map(r => r.year))].map(year => {
                    const found = deviceInsights.deviceTypeByYear.find(r => r.year === year && r.device_type === type);
                    return found?.cnt || 0;
                  }),
                  backgroundColor: ['#00d4ff','#7c83fd','#00e676','#ffab40'][i],
                  borderRadius: 3
                }))
              }}
              options={{ ...CHART_OPTS, responsive: true }}
            />
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Vendor Preference by Course (Top 50)</span></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Course</th><th>Vendor</th><th>Devices</th></tr></thead>
                <tbody>
                  {deviceInsights.vendorByCourse.map((r, i) => (
                    <tr key={i}><td>{r.course}</td><td>{r.vendor_name}</td><td>{r.cnt}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Registration Patterns */}
      {tab === 'patterns' && regPatterns && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Registration Number Pattern Analysis</span>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 16 }}>
            Inferred year and department from registration number structure (e.g. STD/2024/CS/0001 → year: 2024, dept: CS)
          </p>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Student ID</th><th>Registration No</th><th>Inferred Year</th><th>Inferred Dept</th></tr></thead>
              <tbody>
                {regPatterns.slice(0, 100).map(r => (
                  <tr key={r.studentId}>
                    <td>{r.studentId}</td>
                    <td className="text-mono">{r.registration_no}</td>
                    <td>{r.inferredYear || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td>{r.inferredDept ? <span className="badge badge-primary">{r.inferredDept}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
